//#region Imports Packages and Libs

import { StringUtils } from "./string_utils";

//#endregion

//#region Constants

/**
 * Default maximum length for error messages
 */
const DEFAULT_MAX_ERROR_LENGTH = 500;

//#endregion

//#region Error Handler Class

/**
 * Utility class for handling and processing errors
 */
export class ErrorHandler {
	//#region Public Methods

	/**
	 * Extracts a meaningful error message from various error types
	 * @param {any} error - The error object
	 * @param {number} maxLength - Optional maximum length for the message
	 * @returns {string} - User-friendly error message
	 */
	public static getErrorMessage(error: any, maxLength: number = DEFAULT_MAX_ERROR_LENGTH): string {
		try {
			// Handle different error types with specialized methods
			if (error instanceof Error) {
				return this.getErrorObjectMessage(error, maxLength);
			}

      // Handle string errors directly
			if (typeof error === "string") {
				return this.getStringErrorMessage(error, maxLength);
			}

      // Handle object errors, which may contain structured information
			if (error && typeof error === "object") {
				return this.getObjectErrorMessage(error, maxLength);
			}

			// Default error message for unknown error types
			return "An unexpected error occurred";
		} catch (e) {
			// Fallback in case error processing itself causes an error
			console.error("[ErrorHandler.getErrorMessage] Error while processing error:", e);
			return "An unexpected error occurred";
		}
	}

	//#endregion

	//#region Private Methods

	/**
	 * Extracts message from Error objects
	 * @param {Error} error - The Error object
	 * @param {number} maxLength - Maximum allowed length for the message
	 * @returns {string} - Formatted error message
	 */
	private static getErrorObjectMessage(error: Error, maxLength: number): string {
		// For Error objects, get the message without the stack trace
		const message = error.message || "";
		return StringUtils.truncateMessage(message, maxLength);
	}

	/**
	 * Handles string error messages
	 * @param {string} errorMessage - The error message string
	 * @param {number} maxLength - Maximum allowed length for the message
	 * @returns {string} - Formatted error message
	 */
	private static getStringErrorMessage(errorMessage: string, maxLength: number): string {
		// For string errors, truncate if too long
		return StringUtils.truncateMessage(errorMessage, maxLength);
	}

	/**
	 * Extracts message from object-type errors
	 * @param {object} errorObject - The error object
	 * @param {number} maxLength - Maximum allowed length for the message
	 * @returns {string} - Formatted error message
	 */
	private static getObjectErrorMessage(errorObject: object, maxLength: number): string {
		// For object errors, try to extract the most relevant information
		if ("message" in errorObject && typeof (errorObject as any).message === "string") {
			return StringUtils.truncateMessage((errorObject as any).message, maxLength);
		}

		// Check for response property (common in API errors)
		if ("response" in errorObject && (errorObject as any).response) {
			const response = (errorObject as any).response;

			// Check for data.message pattern (common in REST APIs)
			if (response.data && typeof response.data.message === "string") {
				return StringUtils.truncateMessage(response.data.message, maxLength);
			}

			// Check for statusText (HTTP status description)
			if (typeof response.statusText === "string") {
				return StringUtils.truncateMessage(`${response.status || ""} ${response.statusText}`.trim(), maxLength);
			}
		}

		// If the object has a reasonable string representation, use it
		try {
			const errorStr = JSON.stringify(errorObject);
			if (errorStr && errorStr !== "{}") {
				return StringUtils.truncateMessage(errorStr, maxLength);
			}
		} catch (e) {
			// JSON stringification failed, ignore
		}
		// If no useful information can be extracted
		return "An unexpected error occurred";
	}

	//#endregion
}

//#endregion
